import { Box, Heading, ListItemSelect, TextButton } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useNext } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import ArrowUpRight from '../../reusable/icons/ArrowUpRight.svg'
import { WixStudioLogoWithBack } from '../../reusable/WixStudioLogoWithBack'
import { wixStudioWelcomeSwitchDataHooks as dataHooks } from './WixStudioWelcomeSwitch.dataHooks'
import s from './WixStudioWelcomeSwitch.scss'

export type Content = {
  back: string
  title: string
  subtitle: string
  question: ChoiceQuestionContentComponent<{ title: string }, { title: string; description: string }>
}

export const WixStudioWelcomeSwitch: FC = () => {
  const { save } = useStorageContext()
  const { content } = useContent<Content>()
  const questionOptionsExceptLast = content.question.options.slice(0, -1)
  const lastQuestionOption = content.question.options[content.question.options.length - 1]
  const pageLogger = usePageLogger()
  const next = useNext()

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const handleBtnClick = (optionKey: string) => () => {
    void pageLogger.answer({ answer: optionKey })
    save({ isWixStudioAccount: true })
    next({
      [content.question.questionKey]: { values: [optionKey] },
    })
  }

  const handleLinkClick = (optionKey: string) => () => {
    void pageLogger.answer({ answer: optionKey })
    next({
      [content.question.questionKey]: { values: [optionKey] },
    })
  }

  return (
    <>
      <Box
        height="100vh"
        background="linear-gradient(180deg, rgba(197, 215, 247, 0.70) 25.43%, rgba(218, 215, 227, 0.70) 55.51%, rgba(243, 244, 225, 0.70) 100%)"
        align="center"
        verticalAlign="middle"
      >
        <WixStudioLogoWithBack />
        <Box margin="0 12%" direction="vertical" align="center">
          <Box direction="vertical" align="center" gap="SP6" textAlign="center">
            <Heading as="h1" className={s.title} dataHook={dataHooks.title()}>
              {content.title}
            </Heading>
            <Box width="586px">
              <Heading className={s.subtitle} dataHook={dataHooks.subtitle()}>
                {content.subtitle}
              </Heading>
            </Box>
          </Box>
          <Box marginTop="67px" direction="vertical" align="center" textAlign="center" gap="30px">
            <Heading as="h2" size="large" dataHook={dataHooks.questionTitle()}>
              {content.question.content.title}
            </Heading>
            <Box gap="24px">
              {questionOptionsExceptLast.map(option => (
                <ListItemSelect
                  key={option.optionKey}
                  onClick={handleBtnClick(option.optionKey)}
                  suffix={<ArrowUpRight color="rgba(0, 6, 36, 1)" />}
                  title={option.title}
                  size="medium"
                  className={s.listItem}
                  subtitle={option.description}
                  dataHook={dataHooks.item({ key: option.optionKey })}
                />
              ))}
            </Box>
          </Box>
          <Box marginTop="120px">
            <TextButton
              underline="always"
              skin="dark"
              onClick={handleLinkClick(lastQuestionOption.optionKey)}
              dataHook={dataHooks.link()}
            >
              {lastQuestionOption.title}
            </TextButton>
          </Box>
        </Box>
      </Box>
    </>
  )
}
